/**
 * Shared timezone utilities (Epic #532 / issue #535 foundation; picker data
 * widened and regrouped for issues #578 + #540).
 *
 * One explicit-IANA resolution chain feeds every surface:
 *   contact.timezone → user.timezone → browser zone → UTC (terminal fallback).
 * Dates are stored as UTC instants; these helpers convert at FORMAT time only,
 * so grid math and naive string contracts elsewhere stay untouched.
 *
 * Formatters wrap date-fns-tz's formatInTimeZone (v3). naiveInZone converts a
 * naive wall-clock string IN a zone to its UTC ISO instant via fromZonedTime.
 *
 * Input contract (#532 residue): the runtime API serialises Mongo datetimes
 * with `datetime.isoformat()`, and Mongo drops tzinfo on write — so a stored
 * UTC instant arrives as "2026-09-14T06:12:27.123000" with NO offset. JS
 * parses such a string as BROWSER-LOCAL wall-clock, which is exactly the
 * "activities still show UTC" symptom. parseInstant() treats an offset-less
 * date-time string as UTC (the storage rule), so every formatter here is
 * correct for both `…Z` / `…+00:00` strings and the naive shape.
 *
 * ── Picker presentation (#578) ────────────────────────────────────────────
 * The curated list (176 zones) is grouped under fixed region headings in
 * TIMEZONE_REGIONS order, offset-ascending within each group, UTC pinned
 * first as its own entry. Offsets are COMPUTED from the tz database via Intl
 * longOffset — never a maintained table — and only on FIRST open of the
 * picker (lazy + memoised), so pre-auth pages never pay 176 Intl
 * constructions at import. Accepted trade: a tab open across a DST boundary
 * shows the pre-transition offset until reload — the label is decorative;
 * the STORED value is always the IANA identifier.
 */
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

// Region headings in #578's fixed order (Africa before Middle East — the
// source comments had the opposite; #578 wins).
export const TIMEZONE_REGIONS = [
    'Americas',
    'Europe',
    'Africa',
    'Middle East',
    'South & Central Asia',
    'East & Southeast Asia',
    'Oceania',
];

// Curated IANA zones (176): UTC pinned first, then TIMEZONE_REGIONS order.
const BASE_TIMEZONES = [
    { value: 'UTC', label: 'UTC', search: 'utc gmt greenwich' },
    // Americas
    { value: 'America/New_York', region: 'Americas', label: 'New York (ET)', country: 'United States', search: 'new york eastern us usa united states et est edt' },
    { value: 'America/Chicago', region: 'Americas', label: 'Chicago (CT)', country: 'United States', search: 'chicago central us usa ct cst cdt dallas houston' },
    { value: 'America/Denver', region: 'Americas', label: 'Denver (MT)', country: 'United States', search: 'denver mountain us usa mt mst mdt' },
    { value: 'America/Los_Angeles', region: 'Americas', label: 'Los Angeles (PT)', country: 'United States', search: 'los angeles pacific us usa pt pst pdt san francisco seattle' },
    { value: 'America/Anchorage', region: 'Americas', label: 'Anchorage (AKT)', country: 'United States', search: 'anchorage alaska us usa' },
    { value: 'Pacific/Honolulu', region: 'Americas', label: 'Honolulu (HST)', country: 'United States', search: 'honolulu hawaii us usa hst' },
    { value: 'America/Toronto', region: 'Americas', label: 'Toronto (ET)', country: 'Canada', search: 'toronto canada ontario eastern' },
    { value: 'America/Vancouver', region: 'Americas', label: 'Vancouver (PT)', country: 'Canada', search: 'vancouver canada british columbia pacific' },
    { value: 'America/Mexico_City', region: 'Americas', label: 'Mexico City (CST)', country: 'Mexico', search: 'mexico city mexico central' },
    { value: 'America/Sao_Paulo', region: 'Americas', label: 'São Paulo (BRT)', country: 'Brazil', search: 'sao paulo brazil brasil' },
    { value: 'America/Buenos_Aires', region: 'Americas', label: 'Buenos Aires (ART)', country: 'Argentina', search: 'buenos aires argentina' },
    { value: 'America/Bogota', region: 'Americas', label: 'Bogotá (COT)', country: 'Colombia', search: 'bogota colombia' },
    { value: 'America/Lima', region: 'Americas', label: 'Lima (PET)', country: 'Peru', search: 'lima peru' },
    { value: 'America/Santiago', region: 'Americas', label: 'Santiago (CLT)', country: 'Chile', search: 'santiago chile' },
    // Europe
    { value: 'Europe/London', region: 'Europe', label: 'London (GMT/BST)', country: 'United Kingdom', search: 'london uk united kingdom england britain gmt bst' },
    { value: 'Europe/Paris', region: 'Europe', label: 'Paris (CET)', country: 'France', search: 'paris france cet cest' },
    { value: 'Europe/Berlin', region: 'Europe', label: 'Berlin (CET)', country: 'Germany', search: 'berlin germany deutschland cet' },
    { value: 'Europe/Madrid', region: 'Europe', label: 'Madrid (CET)', country: 'Spain', search: 'madrid spain españa' },
    { value: 'Europe/Rome', region: 'Europe', label: 'Rome (CET)', country: 'Italy', search: 'rome italy italia milan' },
    { value: 'Europe/Amsterdam', region: 'Europe', label: 'Amsterdam (CET)', country: 'Netherlands', search: 'amsterdam netherlands holland' },
    { value: 'Europe/Brussels', region: 'Europe', label: 'Brussels (CET)', country: 'Belgium', search: 'brussels belgium' },
    { value: 'Europe/Zurich', region: 'Europe', label: 'Zurich (CET)', country: 'Switzerland', search: 'zurich switzerland geneva' },
    { value: 'Europe/Vienna', region: 'Europe', label: 'Vienna (CET)', country: 'Austria', search: 'vienna austria' },
    { value: 'Europe/Stockholm', region: 'Europe', label: 'Stockholm (CET)', country: 'Sweden', search: 'stockholm sweden' },
    { value: 'Europe/Oslo', region: 'Europe', label: 'Oslo (CET)', country: 'Norway', search: 'oslo norway' },
    { value: 'Europe/Copenhagen', region: 'Europe', label: 'Copenhagen (CET)', country: 'Denmark', search: 'copenhagen denmark' },
    { value: 'Europe/Helsinki', region: 'Europe', label: 'Helsinki (EET)', country: 'Finland', search: 'helsinki finland eet' },
    { value: 'Europe/Warsaw', region: 'Europe', label: 'Warsaw (CET)', country: 'Poland', search: 'warsaw poland' },
    { value: 'Europe/Prague', region: 'Europe', label: 'Prague (CET)', country: 'Czechia', search: 'prague czech czechia' },
    { value: 'Europe/Budapest', region: 'Europe', label: 'Budapest (CET)', country: 'Hungary', search: 'budapest hungary' },
    { value: 'Europe/Bucharest', region: 'Europe', label: 'Bucharest (EET)', country: 'Romania', search: 'bucharest romania eet' },
    { value: 'Europe/Athens', region: 'Europe', label: 'Athens (EET)', country: 'Greece', search: 'athens greece eet' },
    { value: 'Europe/Istanbul', region: 'Europe', label: 'Istanbul (TRT)', country: 'Türkiye', search: 'istanbul turkey türkiye trt' },
    { value: 'Europe/Moscow', region: 'Europe', label: 'Moscow (MSK)', country: 'Russia', search: 'moscow russia msk' },
    { value: 'Europe/Lisbon', region: 'Europe', label: 'Lisbon (WET)', country: 'Portugal', search: 'lisbon portugal wet' },
    { value: 'Europe/Dublin', region: 'Europe', label: 'Dublin (GMT/IST)', country: 'Ireland', search: 'dublin ireland' },
    // Middle East
    { value: 'Asia/Dubai', region: 'Middle East', label: 'Dubai (GST)', country: 'United Arab Emirates', search: 'dubai uae united arab emirates abu dhabi gst gulf' },
    { value: 'Asia/Qatar', region: 'Middle East', label: 'Doha (AST)', country: 'Qatar', search: 'doha qatar ast arabian' },
    { value: 'Asia/Riyadh', region: 'Middle East', label: 'Riyadh (AST)', country: 'Saudi Arabia', search: 'riyadh saudi arabia ksa jeddah ast' },
    { value: 'Asia/Kuwait', region: 'Middle East', label: 'Kuwait (AST)', country: 'Kuwait', search: 'kuwait ast' },
    { value: 'Asia/Bahrain', region: 'Middle East', label: 'Bahrain (AST)', country: 'Bahrain', search: 'bahrain manama ast' },
    { value: 'Asia/Muscat', region: 'Middle East', label: 'Muscat (GST)', country: 'Oman', search: 'muscat oman gst' },
    { value: 'Asia/Tehran', region: 'Middle East', label: 'Tehran (IRST)', country: 'Iran', search: 'tehran iran irst' },
    { value: 'Asia/Jerusalem', region: 'Middle East', label: 'Jerusalem (IST)', country: 'Israel', search: 'jerusalem israel tel aviv ist' },
    { value: 'Asia/Beirut', region: 'Middle East', label: 'Beirut (EET)', country: 'Lebanon', search: 'beirut lebanon eet' },
    { value: 'Asia/Amman', region: 'Middle East', label: 'Amman (EET)', country: 'Jordan', search: 'amman jordan eet' },
    { value: 'Asia/Baghdad', region: 'Middle East', label: 'Baghdad (AST)', country: 'Iraq', search: 'baghdad iraq ast' },
    // Africa
    { value: 'Africa/Cairo', region: 'Africa', label: 'Cairo (EET)', country: 'Egypt', search: 'cairo egypt eet' },
    { value: 'Africa/Lagos', region: 'Africa', label: 'Lagos (WAT)', country: 'Nigeria', search: 'lagos nigeria west africa wat' },
    { value: 'Africa/Johannesburg', region: 'Africa', label: 'Johannesburg (SAST)', country: 'South Africa', search: 'johannesburg south africa cape town sast' },
    { value: 'Africa/Nairobi', region: 'Africa', label: 'Nairobi (EAT)', country: 'Kenya', search: 'nairobi kenya east africa eat' },
    { value: 'Africa/Casablanca', region: 'Africa', label: 'Casablanca (WET)', country: 'Morocco', search: 'casablanca morocco wet' },
    // South & Central Asia
    { value: 'Asia/Kolkata', region: 'South & Central Asia', label: 'Mumbai / Delhi (IST)', country: 'India', search: 'mumbai delhi kolkata india ist chennai bangalore hyderabad' },
    { value: 'Asia/Karachi', region: 'South & Central Asia', label: 'Karachi (PKT)', country: 'Pakistan', search: 'karachi pakistan lahore islamabad pkt' },
    { value: 'Asia/Dhaka', region: 'South & Central Asia', label: 'Dhaka (BST)', country: 'Bangladesh', search: 'dhaka bangladesh bst' },
    { value: 'Asia/Colombo', region: 'South & Central Asia', label: 'Colombo (IST)', country: 'Sri Lanka', search: 'colombo sri lanka' },
    // East & Southeast Asia
    { value: 'Asia/Singapore', region: 'East & Southeast Asia', label: 'Singapore (SGT)', country: 'Singapore', search: 'singapore sgt' },
    { value: 'Asia/Hong_Kong', region: 'East & Southeast Asia', label: 'Hong Kong (HKT)', country: 'Hong Kong', search: 'hong kong hkt china' },
    { value: 'Asia/Shanghai', region: 'East & Southeast Asia', label: 'Shanghai (CST)', country: 'China', search: 'shanghai china beijing cst' },
    { value: 'Asia/Tokyo', region: 'East & Southeast Asia', label: 'Tokyo (JST)', country: 'Japan', search: 'tokyo japan jst osaka' },
    { value: 'Asia/Seoul', region: 'East & Southeast Asia', label: 'Seoul (KST)', country: 'South Korea', search: 'seoul korea south korea kst' },
    { value: 'Asia/Taipei', region: 'East & Southeast Asia', label: 'Taipei (CST)', country: 'Taiwan', search: 'taipei taiwan' },
    { value: 'Asia/Bangkok', region: 'East & Southeast Asia', label: 'Bangkok (ICT)', country: 'Thailand', search: 'bangkok thailand ict' },
    { value: 'Asia/Ho_Chi_Minh', region: 'East & Southeast Asia', label: 'Ho Chi Minh City (ICT)', country: 'Vietnam', search: 'ho chi minh vietnam saigon ict' },
    { value: 'Asia/Jakarta', region: 'East & Southeast Asia', label: 'Jakarta (WIB)', country: 'Indonesia', search: 'jakarta indonesia wib' },
    { value: 'Asia/Manila', region: 'East & Southeast Asia', label: 'Manila (PHT)', country: 'Philippines', search: 'manila philippines pht' },
    { value: 'Asia/Kuala_Lumpur', region: 'East & Southeast Asia', label: 'Kuala Lumpur (MYT)', country: 'Malaysia', search: 'kuala lumpur malaysia myt' },
    // Oceania
    { value: 'Australia/Sydney', region: 'Oceania', label: 'Sydney (AEST)', country: 'Australia', search: 'sydney australia nsw aest' },
    { value: 'Australia/Melbourne', region: 'Oceania', label: 'Melbourne (AEST)', country: 'Australia', search: 'melbourne australia victoria aest' },
    { value: 'Australia/Brisbane', region: 'Oceania', label: 'Brisbane (AEST)', country: 'Australia', search: 'brisbane australia queensland aest' },
    { value: 'Australia/Perth', region: 'Oceania', label: 'Perth (AWST)', country: 'Australia', search: 'perth australia western awst' },
    { value: 'Australia/Adelaide', region: 'Oceania', label: 'Adelaide (ACST)', country: 'Australia', search: 'adelaide australia south acst' },
    { value: 'Pacific/Auckland', region: 'Oceania', label: 'Auckland (NZST)', country: 'New Zealand', search: 'auckland new zealand nz nzst' },
    { value: 'Pacific/Fiji', region: 'Oceania', label: 'Fiji (FJT)', country: 'Fiji', search: 'fiji fjt' },
    // Americas (widened for #578)
    { value: 'America/Halifax', region: 'Americas', label: 'Halifax (AT)', country: 'Canada', search: 'halifax atlantic canada nova scotia' },
    { value: 'America/St_Johns', region: 'Americas', label: 'St. John\'s (NT)', country: 'Canada', search: 'st johns newfoundland canada nt' },
    { value: 'America/Winnipeg', region: 'Americas', label: 'Winnipeg (CT)', country: 'Canada', search: 'winnipeg manitoba canada central' },
    { value: 'America/Edmonton', region: 'Americas', label: 'Edmonton (MT)', country: 'Canada', search: 'edmonton alberta canada mountain' },
    { value: 'America/Phoenix', region: 'Americas', label: 'Phoenix (MST)', country: 'United States', search: 'phoenix arizona us usa mst' },
    { value: 'America/Tijuana', region: 'Americas', label: 'Tijuana (PT)', country: 'Mexico', search: 'tijuana mexico pacific baja' },
    { value: 'America/Monterrey', region: 'Americas', label: 'Monterrey (CT)', country: 'Mexico', search: 'monterrey mexico central' },
    { value: 'America/Guatemala', region: 'Americas', label: 'Guatemala City (CT)', country: 'Guatemala', search: 'guatemala city central america' },
    { value: 'America/El_Salvador', region: 'Americas', label: 'San Salvador (CT)', country: 'El Salvador', search: 'san salvador el salvador central america' },
    { value: 'America/Tegucigalpa', region: 'Americas', label: 'Tegucigalpa (CT)', country: 'Honduras', search: 'tegucigalpa honduras central america' },
    { value: 'America/Managua', region: 'Americas', label: 'Managua (CT)', country: 'Nicaragua', search: 'managua nicaragua central america' },
    { value: 'America/Costa_Rica', region: 'Americas', label: 'San José (CT)', country: 'Costa Rica', search: 'san jose costa rica central america' },
    { value: 'America/Panama', region: 'Americas', label: 'Panama (EST)', country: 'Panama', search: 'panama city panama' },
    { value: 'America/Belize', region: 'Americas', label: 'Belmopan (CST)', country: 'Belize', search: 'belmopan belize central america' },
    { value: 'America/Havana', region: 'Americas', label: 'Havana (CST)', country: 'Cuba', search: 'havana cuba' },
    { value: 'America/Santo_Domingo', region: 'Americas', label: 'Santo Domingo (AST)', country: 'Dominican Republic', search: 'santo domingo dominican republic' },
    { value: 'America/Puerto_Rico', region: 'Americas', label: 'San Juan (AST)', country: 'Puerto Rico', search: 'san juan puerto rico' },
    { value: 'America/Jamaica', region: 'Americas', label: 'Kingston (EST)', country: 'Jamaica', search: 'kingston jamaica' },
    { value: 'America/Caracas', region: 'Americas', label: 'Caracas (VET)', country: 'Venezuela', search: 'caracas venezuela' },
    { value: 'America/Guayaquil', region: 'Americas', label: 'Guayaquil (ECT)', country: 'Ecuador', search: 'guayaquil ecuador quito' },
    { value: 'America/La_Paz', region: 'Americas', label: 'La Paz (BOT)', country: 'Bolivia', search: 'la paz bolivia' },
    { value: 'America/Asuncion', region: 'Americas', label: 'Asunción (PYT)', country: 'Paraguay', search: 'asuncion paraguay' },
    { value: 'America/Montevideo', region: 'Americas', label: 'Montevideo (UYT)', country: 'Uruguay', search: 'montevideo uruguay' },
    { value: 'America/Guyana', region: 'Americas', label: 'Georgetown (GYT)', country: 'Guyana', search: 'georgetown guyana' },
    { value: 'America/Paramaribo', region: 'Americas', label: 'Paramaribo (SRT)', country: 'Suriname', search: 'paramaribo suriname' },
    { value: 'America/Adak', region: 'Americas', label: 'Adak (HST)', country: 'United States', search: 'adak alaska us usa aleutian' },
    { value: 'Pacific/Midway', region: 'Americas', label: 'Midway (SST)', country: 'United States Minor Outlying Islands', search: 'midway us usa samoa' },
    // Europe (widened for #578)
    { value: 'Europe/Kyiv', region: 'Europe', label: 'Kyiv (EET)', country: 'Ukraine', search: 'kyiv kiev ukraine eet' },
    { value: 'Atlantic/Reykjavik', region: 'Europe', label: 'Reykjavik (GMT)', country: 'Iceland', search: 'reykjavik iceland gmt' },
    { value: 'Europe/Sofia', region: 'Europe', label: 'Sofia (EET)', country: 'Bulgaria', search: 'sofia bulgaria eet' },
    { value: 'Europe/Belgrade', region: 'Europe', label: 'Belgrade (CET)', country: 'Serbia', search: 'belgrade serbia cet' },
    { value: 'Europe/Zagreb', region: 'Europe', label: 'Zagreb (CET)', country: 'Croatia', search: 'zagreb croatia cet' },
    { value: 'Europe/Sarajevo', region: 'Europe', label: 'Sarajevo (CET)', country: 'Bosnia and Herzegovina', search: 'sarajevo bosnia cet' },
    { value: 'Europe/Ljubljana', region: 'Europe', label: 'Ljubljana (CET)', country: 'Slovenia', search: 'ljubljana slovenia cet' },
    { value: 'Europe/Bratislava', region: 'Europe', label: 'Bratislava (CET)', country: 'Slovakia', search: 'bratislava slovakia cet' },
    { value: 'Europe/Vilnius', region: 'Europe', label: 'Vilnius (EET)', country: 'Lithuania', search: 'vilnius lithuania eet' },
    { value: 'Europe/Riga', region: 'Europe', label: 'Riga (EET)', country: 'Latvia', search: 'riga latvia eet' },
    { value: 'Europe/Tallinn', region: 'Europe', label: 'Tallinn (EET)', country: 'Estonia', search: 'tallinn estonia eet' },
    { value: 'Europe/Minsk', region: 'Europe', label: 'Minsk (MSK)', country: 'Belarus', search: 'minsk belarus msk' },
    { value: 'Europe/Chisinau', region: 'Europe', label: 'Chișinău (EET)', country: 'Moldova', search: 'chisinau moldova eet' },
    { value: 'Europe/Skopje', region: 'Europe', label: 'Skopje (CET)', country: 'North Macedonia', search: 'skopje north macedonia cet' },
    { value: 'Europe/Malta', region: 'Europe', label: 'Valletta (CET)', country: 'Malta', search: 'valletta malta cet' },
    { value: 'Europe/Luxembourg', region: 'Europe', label: 'Luxembourg (CET)', country: 'Luxembourg', search: 'luxembourg cet' },
    // Africa (widened for #578)
    { value: 'Africa/Accra', region: 'Africa', label: 'Accra (GMT)', country: 'Ghana', search: 'accra ghana gmt' },
    { value: 'Africa/Abidjan', region: 'Africa', label: 'Abidjan (GMT)', country: 'Cote d\'Ivoire', search: 'abidjan ivory coast cote divoire gmt' },
    { value: 'Africa/Dakar', region: 'Africa', label: 'Dakar (GMT)', country: 'Senegal', search: 'dakar senegal gmt' },
    { value: 'Africa/Algiers', region: 'Africa', label: 'Algiers (CET)', country: 'Algeria', search: 'algiers algeria cet' },
    { value: 'Africa/Tunis', region: 'Africa', label: 'Tunis (CET)', country: 'Tunisia', search: 'tunis tunisia cet' },
    { value: 'Africa/Tripoli', region: 'Africa', label: 'Tripoli (EET)', country: 'Libya', search: 'tripoli libya eet' },
    { value: 'Africa/Khartoum', region: 'Africa', label: 'Khartoum (CAT)', country: 'Sudan', search: 'khartoum sudan cat' },
    { value: 'Africa/Addis_Ababa', region: 'Africa', label: 'Addis Ababa (EAT)', country: 'Ethiopia', search: 'addis ababa ethiopia eat' },
    { value: 'Africa/Kampala', region: 'Africa', label: 'Kampala (EAT)', country: 'Uganda', search: 'kampala uganda eat' },
    { value: 'Africa/Dar_es_Salaam', region: 'Africa', label: 'Dar es Salaam (EAT)', country: 'Tanzania', search: 'dar es salaam tanzania eat' },
    { value: 'Africa/Kinshasa', region: 'Africa', label: 'Kinshasa (WAT)', country: 'DR Congo', search: 'kinshasa congo wat' },
    { value: 'Africa/Luanda', region: 'Africa', label: 'Luanda (WAT)', country: 'Angola', search: 'luanda angola wat' },
    { value: 'Africa/Harare', region: 'Africa', label: 'Harare (CAT)', country: 'Zimbabwe', search: 'harare zimbabwe cat' },
    { value: 'Africa/Lusaka', region: 'Africa', label: 'Lusaka (CAT)', country: 'Zambia', search: 'lusaka zambia cat' },
    { value: 'Africa/Maputo', region: 'Africa', label: 'Maputo (CAT)', country: 'Mozambique', search: 'maputo mozambique cat' },
    { value: 'Africa/Windhoek', region: 'Africa', label: 'Windhoek (CAT)', country: 'Namibia', search: 'windhoek namibia cat' },
    { value: 'Africa/Gaborone', region: 'Africa', label: 'Gaborone (CAT)', country: 'Botswana', search: 'gaborone botswana cat' },
    { value: 'Africa/Kigali', region: 'Africa', label: 'Kigali (CAT)', country: 'Rwanda', search: 'kigali rwanda cat' },
    { value: 'Africa/Douala', region: 'Africa', label: 'Douala (WAT)', country: 'Cameroon', search: 'douala cameroon wat' },
    { value: 'Africa/Bamako', region: 'Africa', label: 'Bamako (GMT)', country: 'Mali', search: 'bamako mali gmt' },
    // Middle East (widened for #578)
    { value: 'Asia/Damascus', region: 'Middle East', label: 'Damascus (EET)', country: 'Syria', search: 'damascus syria eet' },
    { value: 'Asia/Aden', region: 'Middle East', label: 'Aden (AST)', country: 'Yemen', search: 'aden yemen ast' },
    { value: 'Asia/Nicosia', region: 'Middle East', label: 'Nicosia (EET)', country: 'Cyprus', search: 'nicosia cyprus eet' },
    // South & Central Asia (widened for #578)
    { value: 'Asia/Kathmandu', region: 'South & Central Asia', label: 'Kathmandu (NPT)', country: 'Nepal', search: 'kathmandu nepal npt' },
    { value: 'Asia/Kabul', region: 'South & Central Asia', label: 'Kabul (AFT)', country: 'Afghanistan', search: 'kabul afghanistan aft' },
    { value: 'Asia/Thimphu', region: 'South & Central Asia', label: 'Thimphu (BTT)', country: 'Bhutan', search: 'thimphu bhutan btt' },
    { value: 'Indian/Maldives', region: 'South & Central Asia', label: 'Malé (MVT)', country: 'Maldives', search: 'male maldives mvt' },
    { value: 'Asia/Tashkent', region: 'South & Central Asia', label: 'Tashkent (UZT)', country: 'Uzbekistan', search: 'tashkent uzbekistan uzt' },
    { value: 'Asia/Almaty', region: 'South & Central Asia', label: 'Almaty (ALMT)', country: 'Kazakhstan', search: 'almaty kazakhstan almt' },
    { value: 'Asia/Bishkek', region: 'South & Central Asia', label: 'Bishkek (KGT)', country: 'Kyrgyzstan', search: 'bishkek kyrgyzstan kgt' },
    { value: 'Asia/Dushanbe', region: 'South & Central Asia', label: 'Dushanbe (TJT)', country: 'Tajikistan', search: 'dushanbe tajikistan tjt' },
    { value: 'Asia/Ashgabat', region: 'South & Central Asia', label: 'Ashgabat (TMT)', country: 'Turkmenistan', search: 'ashgabat turkmenistan tmt' },
    { value: 'Asia/Baku', region: 'South & Central Asia', label: 'Baku (AZT)', country: 'Azerbaijan', search: 'baku azerbaijan azt' },
    { value: 'Asia/Tbilisi', region: 'South & Central Asia', label: 'Tbilisi (GET)', country: 'Georgia', search: 'tbilisi georgia get' },
    { value: 'Asia/Yerevan', region: 'South & Central Asia', label: 'Yerevan (AMT)', country: 'Armenia', search: 'yerevan armenia amt' },
    // East & Southeast Asia (widened for #578)
    { value: 'Asia/Phnom_Penh', region: 'East & Southeast Asia', label: 'Phnom Penh (ICT)', country: 'Cambodia', search: 'phnom penh cambodia ict' },
    { value: 'Asia/Vientiane', region: 'East & Southeast Asia', label: 'Vientiane (ICT)', country: 'Laos', search: 'vientiane laos ict' },
    { value: 'Asia/Yangon', region: 'East & Southeast Asia', label: 'Yangon (MMT)', country: 'Myanmar', search: 'yangon myanmar burma mmt rangoon' },
    { value: 'Asia/Ulaanbaatar', region: 'East & Southeast Asia', label: 'Ulaanbaatar (ULAT)', country: 'Mongolia', search: 'ulaanbaatar mongolia ulat' },
    { value: 'Asia/Brunei', region: 'East & Southeast Asia', label: 'Bandar Seri Begawan (BNT)', country: 'Brunei', search: 'bandar seri begawan brunei bnt' },
    { value: 'Asia/Macau', region: 'East & Southeast Asia', label: 'Macau (CST)', country: 'Macau', search: 'macau china cst' },
    { value: 'Asia/Yekaterinburg', region: 'East & Southeast Asia', label: 'Yekaterinburg (YEKT)', country: 'Russia', search: 'yekaterinburg russia yekt' },
    { value: 'Asia/Novosibirsk', region: 'East & Southeast Asia', label: 'Novosibirsk (NOVT)', country: 'Russia', search: 'novosibirsk russia novt' },
    { value: 'Asia/Vladivostok', region: 'East & Southeast Asia', label: 'Vladivostok (VLAT)', country: 'Russia', search: 'vladivostok russia vlat' },
    // Oceania (widened for #578)
    { value: 'Australia/Darwin', region: 'Oceania', label: 'Darwin (ACST)', country: 'Australia', search: 'darwin australia northern territory acst' },
    { value: 'Australia/Hobart', region: 'Oceania', label: 'Hobart (AEST)', country: 'Australia', search: 'hobart australia tasmania aest' },
    { value: 'Australia/Eucla', region: 'Oceania', label: 'Eucla (ACWST)', country: 'Australia', search: 'eucla australia western acwst' },
    { value: 'Pacific/Port_Moresby', region: 'Oceania', label: 'Port Moresby (PGT)', country: 'Papua New Guinea', search: 'port moresby papua new guinea pgt' },
    { value: 'Pacific/Guam', region: 'Oceania', label: 'Hagåtña (ChST)', country: 'Guam', search: 'hagatna hagatna guam chst' },
    { value: 'Pacific/Noumea', region: 'Oceania', label: 'Nouméa (NCT)', country: 'New Caledonia', search: 'noumea new caledonia nct' },
    { value: 'Pacific/Apia', region: 'Oceania', label: 'Apia (WST)', country: 'Samoa', search: 'apia samoa wst' },
    { value: 'Pacific/Tongatapu', region: 'Oceania', label: 'Nukuʻalofa (TOT)', country: 'Tonga', search: 'nukualofa tonga tot' },
    { value: 'Pacific/Guadalcanal', region: 'Oceania', label: 'Honiara (SBT)', country: 'Solomon Islands', search: 'honiara solomon islands sbt' },
    { value: 'Pacific/Efate', region: 'Oceania', label: 'Port Vila (VUT)', country: 'Vanuatu', search: 'port vila vanuatu vut' },
    { value: 'Pacific/Tahiti', region: 'Oceania', label: 'Papeete (TAHT)', country: 'French Polynesia', search: 'papeete tahiti french polynesia taht' },
    { value: 'Pacific/Pago_Pago', region: 'Oceania', label: 'Pago Pago (SST)', country: 'American Samoa', search: 'pago pago american samoa' },
    { value: 'Pacific/Kiritimati', region: 'Oceania', label: 'Kiritimati (LINT)', country: 'Kiribati', search: 'kiritimati christmas island kiribati lint' },
    { value: 'Pacific/Chatham', region: 'Oceania', label: 'Chatham Islands (CHAST)', country: 'New Zealand', search: 'chatham islands new zealand chast' },
];

// Onboarding.jsx's labels, folded in as search aliases (not dropped).
const ONBOARDING_SEARCH_ALIASES = {
    'UTC': 'UTC (Coordinated Universal Time)',
    'America/New_York': 'US Eastern (ET)',
    'America/Chicago': 'US Central (CT)',
    'America/Denver': 'US Mountain (MT)',
    'America/Los_Angeles': 'US Pacific (PT)',
    'Europe/London': 'London (GMT/BST)',
    'Europe/Paris': 'Paris (CET/CEST)',
    'Asia/Singapore': 'Singapore (SGT)',
    'Asia/Tokyo': 'Tokyo (JST)',
    'Australia/Sydney': 'Sydney (AEST/AEDT)',
};

// Intl.DateTimeFormat construction is the expensive part of zone offset
// lookup. Keep the cache lazy (no formatter is created at import), bounded to
// the curated list size, and LRU so arbitrary caller-provided zones cannot
// grow it without limit. The formatter itself remains safe to reuse across
// dates: formatToParts(at) still applies the zone's offset for that instant.
const OFFSET_FORMATTER_CACHE = new Map();
const MAX_OFFSET_FORMATTER_CACHE_SIZE = BASE_TIMEZONES.length;

function getOffsetFormatter(zone) {
    const cached = OFFSET_FORMATTER_CACHE.get(zone);
    if (cached) {
        // Refresh recency so a small number of custom zones cannot evict the
        // prepared picker zones unnecessarily.
        OFFSET_FORMATTER_CACHE.delete(zone);
        OFFSET_FORMATTER_CACHE.set(zone, cached);
        return cached;
    }

    const formatter = new Intl.DateTimeFormat('en', {
        timeZone: zone,
        timeZoneName: 'longOffset',
    });
    OFFSET_FORMATTER_CACHE.set(zone, formatter);
    if (OFFSET_FORMATTER_CACHE.size > MAX_OFFSET_FORMATTER_CACHE_SIZE) {
        const oldestZone = OFFSET_FORMATTER_CACHE.keys().next().value;
        OFFSET_FORMATTER_CACHE.delete(oldestZone);
    }
    return formatter;
}


/**
 * The zone's CURRENT offset in minutes east of UTC, read from the tz
 * database via Intl — the offset shown MOVES at a DST boundary, which is
 * correct (#578: never hardcode). Returns 0 for anything unparseable; the
 * data-integrity test loop is what catches a bad zone, not this.
 */
export function zoneOffsetMinutes(zone, at = new Date()) {
    try {
        const name = getOffsetFormatter(zone)
            .formatToParts(at)
            .find((p) => p.type === 'timeZoneName')?.value || 'GMT+00:00';
        const m = /^GMT([+-])(\d{2}):(\d{2})$/.exec(name);
        if (!m) return 0;                       // bare "GMT" == +00:00
        const sign = m[1] === '-' ? -1 : 1;
        return sign * (Number(m[2]) * 60 + Number(m[3]));
    } catch {
        return 0;
    }
}

/** "(GMT+8)" · "(GMT+5:30)" · "(GMT+5:45)" · "(GMT-4)" · "(GMT+0)" */
export function formatGmtOffset(minutes) {
    const sign = minutes < 0 ? '-' : '+';
    const abs = Math.abs(minutes);
    const h = Math.floor(abs / 60);
    const mm = abs % 60;
    return `(GMT${sign}${h}${mm ? `:${String(mm).padStart(2, '0')}` : ''})`;
}

/** lowercase + NFKD diacritic fold ("São Paulo" → "sao paulo"). */
export function normalizeSearchText(s) {
    return (s || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
}

// ── Lazy, memoised zone preparation (#578 performance pillar) ─────────────

let _preparedCache = null;

/**
 * All 176 zones with derived fields (region, country, offsetMinutes,
 * displayLabel) attached. Computed on FIRST call, then reused for the
 * module's lifetime — keystrokes read the cache, so per-keystroke work is a
 * filter over prepared objects. Never call this at module scope: on the two
 * pre-auth surfaces (Onboarding, PublicBooking) the ~7 ms of Intl work must
 * land on the picker's first open, not first paint.
 */
export function getPreparedZones() {
    if (_preparedCache) return _preparedCache;
    const prepared = BASE_TIMEZONES.map((tz) => {
        const offsetMinutes = tz.value === 'UTC' ? 0 : zoneOffsetMinutes(tz.value);
        const alias = ONBOARDING_SEARCH_ALIASES[tz.value];
        const search = alias ? `${tz.search} | ${alias}` : tz.search;
        return {
            ...tz,
            offsetMinutes,
            displayLabel: tz.value === 'UTC'
                ? 'UTC'
                : `${formatGmtOffset(offsetMinutes)} ${tz.label}`,
            ...(alias ? { search } : {}),
        };
    });
    const REGION_INDEX = new Map(TIMEZONE_REGIONS.map((r, i) => [r, i]));
    _preparedCache = [
        prepared.find((tz) => tz.value === 'UTC'),
        ...prepared
            .filter((tz) => tz.value !== 'UTC')
            .sort((a, b) => {
                const r = REGION_INDEX.get(a.region) - REGION_INDEX.get(b.region);
                if (r !== 0) return r;
                const o = a.offsetMinutes - b.offsetMinutes;
                if (o !== 0) return o;
                return a.label.localeCompare(b.label);
            }),
    ];
    // Keep the historical CANONICAL_TIMEZONES export pointing at the same
    // array (§1.7: name and export survive; §1.6-revised: materialised here,
    // lazily — never at module scope).
    CANONICAL_TIMEZONES.splice(0, CANONICAL_TIMEZONES.length, ..._preparedCache);
    return _preparedCache;
}

/**
 * The full curated list in canonical order (UTC first, regions in
 * TIMEZONE_REGIONS order, offsets ascending within each region), with all
 * derived fields attached.
 *
 * Contract note (#578): this export keeps its historical NAME — existing
 * call sites and suites import it — but per the performance pillar the Intl
 * offset pass is LAZY: the array is FILLED on the first getPreparedZones()
 * call, not at import. Reading it before the picker has ever opened yields
 * an empty array, so NEVER render from it — call getPreparedZones() instead.
 */
export const CANONICAL_TIMEZONES = [];

/** The rendered label for a zone entry ("(GMT+8) Manila (PHT)"; "UTC" for UTC). */
export function timezoneDisplayLabel(tz) {
    if (tz.value === 'UTC') return 'UTC';
    const offsetMinutes = tz.offsetMinutes ?? zoneOffsetMinutes(tz.value);
    return `${formatGmtOffset(offsetMinutes)} ${tz.label}`;
}

// value -> curated label, built on first use. A plain lookup, with none of the Intl work `getPreparedZones()` does.
let _labelByZone = null;

/**
 * The guest-facing label of an IANA zone AT ONE INSTANT: "(GMT+8) Manila (PHT)", "(GMT+9) Tokyo (JST)".
 *
 * `timezoneDisplayLabel(tz)` is the picker's helper: it takes a zone ENTRY, and reads the offset in force today.
 * A booking page quotes a meeting on a date, so this one reads the offset on that date: a New York meeting on
 * 2026-11-10 is "(GMT-5)" even when it is booked in October. It never calls `getPreparedZones()` (the 176-zone Intl pass
 * that #578 keeps off the public pages' first paint), so `CANONICAL_TIMEZONES` stays empty.
 *
 * A valid zone outside the curated list falls back to its IANA name with underscores as spaces ("(GMT-4)
 * America/Indiana/Indianapolis"). 'UTC' is "UTC". An empty or unknown zone is '', so callers can render no label.
 * The backend's `utils.timezone.zone_label_at` returns the same strings for the emails.
 */
export function zoneLabelAt(zone, instant) {
    if (typeof zone !== 'string') return '';
    const candidate = zone.trim();
    if (!candidate || !isValidTimeZone(candidate)) return '';
    if (candidate === 'UTC') return 'UTC';
    if (!_labelByZone) _labelByZone = new Map(BASE_TIMEZONES.map((tz) => [tz.value, tz.label]));
    const label = _labelByZone.get(candidate) || candidate.replace(/_/g, ' ');
    const at = parseInstant(instant) || new Date();
    return `${formatGmtOffset(zoneOffsetMinutes(candidate, at))} ${label}`;
}

/**
 * Group prepared zones as [{ region, zones: [...] }] in TIMEZONE_REGIONS
 * order, UTC first as its own single-entry group. Group renderers drop
 * regions whose filtered zone array is empty.
 */
export function groupedTimezones(zones) {
    const groups = [{ region: 'UTC', zones: zones.filter((tz) => tz.value === 'UTC') }];
    for (const region of TIMEZONE_REGIONS) {
        groups.push({ region, zones: zones.filter((tz) => tz.region === region && tz.value !== 'UTC') });
    }
    // Bare {value,label} entries passed through the `zones` prop carry no
    // region (§1.7: derived fields are optional at render time). Give them a
    // trailing home instead of silently dropping them from the list.
    const known = new Set(TIMEZONE_REGIONS);
    const other = zones.filter((tz) => tz.value !== 'UTC' && !known.has(tz.region));
    if (other.length) groups.push({ region: 'Other', zones: other });
    return groups;
}

// ── Search predicate (#578: city / country / abbreviation / offset, plus the
// user-added region axis) ──────────────────────────────────────────────────

// A pure offset query ("+8", "gmt+8", "8", "-5", "+5:30") is matched against
// the numeric offset, NOT by substring — otherwise "+1" would also hit
// +10..+14. Sign-less means EAST ("5" == "+5"); west requires an explicit
// "-". "−" (U+2212) and "‑" (U+2011) are normalised to ASCII "-" first, so a
// zone name pasted from a document still works.
const OFFSET_QUERY_RE = /^(?:gmt)?\s*([+-]?)(\d{1,2})(?::?(\d{2}))?$/;

function parseOffsetQuery(token) {
    const m = OFFSET_QUERY_RE.exec(token);
    if (!m) return null;
    const sign = m[1] === '-' ? -1 : 1;
    return sign * (Number(m[2]) * 60 + Number(m[3] || 0));
}

/**
 * The single search predicate shared by the picker and its tests. Every
 * whitespace-separated token must match (so "manila ph" narrows); a token
 * that parses as an offset is compared numerically against offsetMinutes;
 * all other tokens substring-match the folded haystack of label, search
 * terms, region, country, IANA id (with separators spaced), display label
 * and offset text. Zones without derived fields (the picker's bare
 * {value,label} override entries) still match on what they have.
 */
export function matchesTimezoneQuery(tz, rawQuery) {
    const q = normalizeSearchText(rawQuery).replace(/[\u2212\u2011]/g, '-').trim();
    if (!q) return true;
    const gmt = formatGmtOffset(tz.offsetMinutes ?? 0);
    const haystack = normalizeSearchText([
        tz.label,
        tz.search,
        tz.region,
        tz.country,
        tz.value,
        typeof tz.value === 'string' ? tz.value.replace(/[/_]/g, ' ') : '',
        tz.displayLabel,
        gmt,
        'gmt' + gmt.slice(4),
        gmt.slice(1),
    ].filter(Boolean).join(' '));
    const offsetMinutes = tz.offsetMinutes ?? 0;
    return q.split(/\s+/).every((tok) => {
        const minutes = parseOffsetQuery(tok);
        if (minutes !== null && tz.offsetMinutes !== undefined) return offsetMinutes === minutes;
        return haystack.includes(tok);
    });
}

/**
 * The viewer's browser zone. Terminal 'UTC' is guaranteed: a missing value or
 * a throwing Intl both fall back.
 */
export function browserTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
        return 'UTC';
    }
}

// Memoised so a render loop never pays an Intl construction per zone check.
// Bounded: the only inputs are profile/contact/browser values, but a stored
// value is free text, so a runaway caller cannot grow it without limit.
const ZONE_VALIDITY = new Map();
const MAX_ZONE_VALIDITY_SIZE = 256;

/**
 * True when `zone` names a zone this runtime's tz database knows. Profile
 * zones are stored unchecked (`PUT /users/profile`), and the date-key helpers
 * (`todayKey`, `formatInTimeZone`) throw on an unknown one, so every chain that
 * feeds them goes through here first.
 */
export function isValidTimeZone(zone) {
    if (typeof zone !== 'string') return false;
    const candidate = zone.trim();
    if (!candidate) return false;
    let valid = ZONE_VALIDITY.get(candidate);
    if (valid === undefined) {
        try {
            new Intl.DateTimeFormat('en', { timeZone: candidate });
            valid = true;
        } catch {
            valid = false;
        }
        if (ZONE_VALIDITY.size >= MAX_ZONE_VALIDITY_SIZE) ZONE_VALIDITY.clear();
        ZONE_VALIDITY.set(candidate, valid);
    }
    return valid;
}

/**
 * The display chain: contact → user → browser → UTC. Empty/whitespace values
 * and zones the runtime does not know (#904) are skipped; unknown-shaped
 * inputs fall through to the browser default.
 */
export function resolveDisplayZone({ contact, user } = {}) {
    const candidates = [contact?.timezone, user?.timezone];
    for (const candidate of candidates) {
        const trimmed = typeof candidate === 'string' ? candidate.trim() : '';
        if (trimmed && isValidTimeZone(trimmed)) return trimmed;
    }
    return browserTimeZone();
}

// "YYYY-MM-DDTHH:mm[:ss[.ffffff]]" with no trailing Z / ±HH:MM — the shape
// Python's isoformat() emits for a tz-naive datetime. A "T" or a space
// separator both count; a date-only "YYYY-MM-DD" does NOT match (it is a
// calendar date, not an instant — see formatInZone's note).
const NAIVE_DATETIME_RE = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2})?)(\.\d+)?$/;

/**
 * Parse an instant (Date, ISO string, epoch ms) into a Date.
 *
 * An offset-less date-time string is interpreted as UTC — the storage rule
 * ("store UTC") applied at the read boundary — instead of the browser-local
 * reading `new Date()` would give it. Fractional seconds are clamped to
 * millisecond precision (Python emits 6 digits; the Date grammar allows 3).
 * Returns null for null/undefined/'' and for anything that does not parse.
 */
export function parseInstant(dateLike) {
    if (dateLike === null || dateLike === undefined || dateLike === '') return null;
    let candidate = dateLike;
    if (typeof dateLike === 'string') {
        const m = NAIVE_DATETIME_RE.exec(dateLike.trim());
        if (m) {
            const frac = m[3] ? m[3].slice(0, 4) : '';
            candidate = `${m[1]}T${m[2]}${frac}Z`;
        }
    }
    const d = new Date(candidate);
    return isNaN(d.getTime()) ? null : d;
}

/**
 * Format an instant (Date, ISO string, epoch ms) in an explicit zone.
 * Returns '' for null/undefined/''/invalid input so callers can fall back.
 *
 * When `zone` is omitted the BROWSER zone is used, never a silent UTC — the
 * epic's terminal fallback is the viewer's real clock. Callers that have a
 * profile in scope should still pass `zone` (see hooks/useDisplayZone.js) so
 * two colleagues in different countries read the same labelled instant.
 *
 * Date-only strings ("YYYY-MM-DD", e.g. a task due_date) are calendar dates,
 * not instants — do not route them through here; `new Date('YYYY-MM-DD')` is
 * midnight UTC and would shift a day in any negative-offset zone.
 */
export function formatInZone(dateLike, { zone, formatStr = 'MMM d, yyyy h:mm a' } = {}) {
    const d = parseInstant(dateLike);
    if (!d) return '';
    try {
        return formatInTimeZone(d, zone || browserTimeZone(), formatStr);
    } catch {
        return '';
    }
}

/** Date-only label in an explicit zone ('MMM d, yyyy'). */
export function formatDateInZone(dateLike, { zone } = {}) {
    return formatInZone(dateLike, { zone, formatStr: 'MMM d, yyyy' });
}

/** Date + time + zone-name label ('MMM d, yyyy h:mm a (zzz)'). */
export function formatRelativeInZone(dateLike, { zone } = {}) {
    return formatInZone(dateLike, { zone, formatStr: 'MMM d, yyyy h:mm a (zzz)' });
}

/**
 * A naive wall-clock string ('YYYY-MM-DDTHH:mm') interpreted IN `zone` →
 * its UTC ISO instant. '' for falsy input.
 */
export function naiveInZone(naiveIso, zone) {
    if (!naiveIso) return '';
    try {
        return fromZonedTime(naiveIso, zone || 'UTC').toISOString();
    } catch {
        return '';
    }
}
